import { useState } from "react";

/**
 * D3: TCPA Compliance Tools
 * Quiet hours config (per timezone), DNC list management, consent timestamps.
 */

interface QuietHoursConfig {
  enabled: boolean;
  startHour: number; // 0-23
  endHour: number;
  timezone: string;
}

interface DncEntry {
  id: string;
  phone: string;
  reason: string;
  addedAt: string;
}

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
] as const;

export default function TcpaCompliancePage() {
  const [quietHours, setQuietHours] = useState<QuietHoursConfig>({
    enabled: true,
    startHour: 8,
    endHour: 21,
    timezone: "America/New_York",
  });
  const [dncList, setDncList] = useState<DncEntry[]>([]);
  const [newPhone, setNewPhone] = useState("");
  const [newReason, setNewReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveQuietHours = async () => {
    setIsSaving(true);
    // In production: POST to /v1/tenant/config with tcpa_quiet_hours key
    await new Promise((r) => setTimeout(r, 300));
    setIsSaving(false);
  };

  const handleAddDnc = () => {
    if (!newPhone) return;
    setDncList((prev) => [...prev, {
      id: crypto.randomUUID(),
      phone: newPhone,
      reason: newReason || "Manual add",
      addedAt: new Date().toISOString(),
    }]);
    setNewPhone("");
    setNewReason("");
  };

  const handleRemoveDnc = (id: string) => {
    setDncList((prev) => prev.filter((e) => e.id !== id));
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
      const entries: DncEntry[] = lines.slice(1).map((line) => {
        const parts = line.split(",").map((s) => s.trim());
        return {
          id: crypto.randomUUID(),
          phone: parts[0] ?? "",
          reason: parts[1] || "CSV import",
          addedAt: new Date().toISOString(),
        };
      });
      setDncList((prev) => [...prev, ...entries]);
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">TCPA Compliance</h1>

      {/* Quiet Hours */}
      <div className="mb-6 rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium">Quiet Hours</h3>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={quietHours.enabled} onChange={(e) => setQuietHours((p) => ({ ...p, enabled: e.target.checked }))} className="rounded" />
            Enabled
          </label>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          SMS messages will be held and delivered after quiet hours end. Required by TCPA.
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Allowed From</label>
            <select value={quietHours.startHour} onChange={(e) => setQuietHours((p) => ({ ...p, startHour: Number(e.target.value) }))} className="w-full rounded-md border border-input px-3 py-2 text-sm">
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{i.toString().padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Allowed Until</label>
            <select value={quietHours.endHour} onChange={(e) => setQuietHours((p) => ({ ...p, endHour: Number(e.target.value) }))} className="w-full rounded-md border border-input px-3 py-2 text-sm">
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{i.toString().padStart(2, "0")}:00</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Timezone</label>
            <select value={quietHours.timezone} onChange={(e) => setQuietHours((p) => ({ ...p, timezone: e.target.value }))} className="w-full rounded-md border border-input px-3 py-2 text-sm">
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
        </div>
        <button onClick={handleSaveQuietHours} disabled={isSaving} className="mt-3 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50">
          {isSaving ? "Saving..." : "Save Quiet Hours"}
        </button>
      </div>

      {/* DNC List */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 font-medium">Do Not Contact (DNC) List</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          Numbers on this list will never receive SMS. Add manually or upload a CSV (columns: phone, reason).
        </p>

        <div className="mb-3 flex gap-2">
          <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="+1XXXXXXXXXX" className="flex-1 rounded-md border border-input px-3 py-2 text-sm" />
          <input value={newReason} onChange={(e) => setNewReason(e.target.value)} placeholder="Reason (optional)" className="flex-1 rounded-md border border-input px-3 py-2 text-sm" />
          <button onClick={handleAddDnc} className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">Add</button>
        </div>

        <div className="mb-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-border px-4 py-2 text-sm hover:bg-accent">
            Upload CSV
            <input type="file" accept=".csv" onChange={handleCsvUpload} className="hidden" />
          </label>
        </div>

        <div className="max-h-64 overflow-y-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left">
                <th className="px-3 py-2 font-medium">Phone</th>
                <th className="px-3 py-2 font-medium">Reason</th>
                <th className="px-3 py-2 font-medium">Added</th>
                <th className="px-3 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {dncList.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">DNC list is empty.</td></tr>
              )}
              {dncList.map((entry) => (
                <tr key={entry.id} className="border-b last:border-0">
                  <td className="px-3 py-2 font-mono text-xs">{entry.phone}</td>
                  <td className="px-3 py-2">{entry.reason}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(entry.addedAt).toLocaleDateString()}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => handleRemoveDnc(entry.id)} className="text-xs text-destructive hover:underline">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{dncList.length} number{dncList.length !== 1 ? "s" : ""} on DNC list</p>
      </div>
    </div>
  );
}
